/**
 * Simple horizontal image carousel for drop photo galleries.
 * Uses CSS scroll-snap for smooth swipe on mobile + buttons on desktop.
 * Lightbox modal opens on click for a full-size view.
 */
import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

export default function ImageCarousel({ images = [], alt = "", testId = "carousel" }) {
  const scroller = useRef(null);
  const [lightbox, setLightbox] = useState(null); // url

  if (!images.length) return null;

  const scroll = (dir) => {
    if (!scroller.current) return;
    const w = scroller.current.clientWidth;
    scroller.current.scrollBy({ left: dir * w * 0.85, behavior: "smooth" });
  };

  return (
    <div data-testid={testId}>
      <div className="relative">
        <div
          ref={scroller}
          className="flex overflow-x-auto snap-x snap-mandatory gap-3 pb-2 scroll-smooth"
          style={{ scrollbarWidth: "thin" }}
          data-testid={`${testId}-scroller`}
        >
          {images.map((url, i) => (
            <button
              key={url + i}
              type="button"
              onClick={() => setLightbox(url)}
              className="flex-none w-[80%] sm:w-[55%] md:w-[45%] snap-start rounded-lg overflow-hidden bg-[var(--ff-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--ff-primary)]"
              data-testid={`${testId}-item-${i}`}
              aria-label={`View image ${i + 1} of ${images.length}`}
            >
              <img src={url} alt={`${alt} ${i + 1}`} className="w-full h-72 object-cover hover:scale-105 transition-transform" loading={i === 0 ? "eager" : "lazy"} onError={(e) => { e.currentTarget.style.display = "none"; }}/>
            </button>
          ))}
        </div>
        {images.length > 1 && (
          <>
            <button type="button" onClick={() => scroll(-1)} className="hidden sm:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 bg-white shadow-lg rounded-full p-2 hover:bg-[var(--ff-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--ff-primary)]" aria-label="Previous" data-testid={`${testId}-prev`}>
              <ChevronLeft size={18}/>
            </button>
            <button type="button" onClick={() => scroll(1)} className="hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 bg-white shadow-lg rounded-full p-2 hover:bg-[var(--ff-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--ff-primary)]" aria-label="Next" data-testid={`${testId}-next`}>
              <ChevronRight size={18}/>
            </button>
          </>
        )}
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4 cursor-zoom-out" onClick={() => setLightbox(null)} data-testid={`${testId}-lightbox`}>
          <button type="button" onClick={() => setLightbox(null)} className="absolute top-4 right-4 text-white hover:text-white/70" aria-label="Close" data-testid={`${testId}-lightbox-close`}>
            <X size={28}/>
          </button>
          <img src={lightbox} alt={alt} className="max-h-[90vh] max-w-[90vw] object-contain rounded shadow-2xl"/>
        </div>
      )}
    </div>
  );
}
